import { IEmailProvider, EmailSendResult, EmailConfig, EmailTemplate } from './types'

// Optional email provider dependencies
// Users must install these packages if they want to use these providers:
// - nodemailer (for SMTP)
// - @sendgrid/mail (for SendGrid)
// - form-data (for Mailgun)

/**
 * Base email provider
 */
abstract class BaseEmailProvider implements IEmailProvider {
  protected config: EmailConfig

  constructor(config: EmailConfig) {
    this.config = config
  }

  protected getTemplate(name: string, code: string, link: string): EmailTemplate {
    throw new Error('Not implemented')
  }

  async sendVerificationCode(email: string, code: string, type: string): Promise<EmailSendResult> {
    const template = this.getVerificationCodeTemplate(code)
    return this.send(email, template)
  }

  async sendPasswordResetLink(email: string, resetLink: string): Promise<EmailSendResult> {
    const template = this.getPasswordResetTemplate(resetLink)
    return this.send(email, template)
  }

  async sendVerificationLink(email: string, verificationLink: string): Promise<EmailSendResult> {
    const template = this.getVerificationLinkTemplate(verificationLink)
    return this.send(email, template)
  }

  async sendLoginLink(email: string, loginLink: string): Promise<EmailSendResult> {
    const template = this.getLoginLinkTemplate(loginLink)
    return this.send(email, template)
  }

  async sendChangeEmailVerification(
    email: string,
    verificationLink: string
  ): Promise<EmailSendResult> {
    const template = this.getChangeEmailTemplate(verificationLink)
    return this.send(email, template)
  }

  protected abstract send(email: string, template: EmailTemplate): Promise<EmailSendResult>

  protected getVerificationCodeTemplate(code: string): EmailTemplate {
    return {
      subject: 'Verify your email',
      html: `
        <h1>Email Verification</h1>
        <p>Your verification code is:</p>
        <h2>${code}</h2>
        <p>This code expires in 1 hour.</p>
      `,
      text: `Your verification code is: ${code}\n\nThis code expires in 1 hour.`
    }
  }

  protected getPasswordResetTemplate(resetLink: string): EmailTemplate {
    return {
      subject: 'Reset your password',
      html: `
        <h1>Password Reset</h1>
        <p>Click the link below to reset your password:</p>
        <a href="${resetLink}">Reset Password</a>
        <p>This link expires in 1 hour.</p>
      `,
      text: `Click the link below to reset your password:\n${resetLink}\n\nThis link expires in 1 hour.`
    }
  }

  protected getVerificationLinkTemplate(verificationLink: string): EmailTemplate {
    return {
      subject: 'Verify your email',
      html: `
        <h1>Email Verification</h1>
        <p>Click the link below to verify your email:</p>
        <a href="${verificationLink}">Verify Email</a>
        <p>This link expires in 1 hour.</p>
      `,
      text: `Click the link below to verify your email:\n${verificationLink}\n\nThis link expires in 1 hour.`
    }
  }

  protected getLoginLinkTemplate(loginLink: string): EmailTemplate {
    return {
      subject: 'Your login link',
      html: `
        <h1>Login Link</h1>
        <p>Click the link below to log in to your account:</p>
        <a href="${loginLink}">Log In</a>
        <p>This link expires in 1 hour.</p>
      `,
      text: `Click the link below to log in to your account:\n${loginLink}\n\nThis link expires in 1 hour.`
    }
  }

  protected getChangeEmailTemplate(verificationLink: string): EmailTemplate {
    return {
      subject: 'Verify your new email address',
      html: `
        <h1>Email Change Verification</h1>
        <p>Click the link below to verify your new email address:</p>
        <a href="${verificationLink}">Verify New Email</a>
        <p>This link expires in 1 hour.</p>
      `,
      text: `Click the link below to verify your new email address:\n${verificationLink}\n\nThis link expires in 1 hour.`
    }
  }
}

/**
 * SMTP Email Provider (Nodemailer)
 */
export class SMTPEmailProvider extends BaseEmailProvider {
  private transporter: any

  constructor(config: EmailConfig) {
    super(config)

    if (!config.smtp) {
      throw new Error('SMTP configuration required for SMTP provider')
    }

    // Dynamically require nodemailer to make it optional
    let nodemailer: any
    try {
      nodemailer = require('nodemailer')
    } catch (e) {
      throw new Error('nodemailer is required for SMTP provider. Install with: npm install nodemailer')
    }

    this.transporter = nodemailer.createTransport({
      host: config.smtp.host,
      port: config.smtp.port,
      secure: config.smtp.secure,
      auth: config.smtp.auth
    })
  }

  protected async send(email: string, template: EmailTemplate): Promise<EmailSendResult> {
    try {
      const info = await this.transporter.sendMail({
        from: `${this.config.fromName || 'Kitium Auth'} <${this.config.from}>`,
        to: email,
        subject: template.subject,
        html: template.html,
        text: template.text
      })

      return {
        success: true,
        messageId: info.messageId
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }
}

/**
 * SendGrid Email Provider
 */
export class SendGridEmailProvider extends BaseEmailProvider {
  private apiKey: string

  constructor(config: EmailConfig) {
    super(config)

    if (!config.apiKey) {
      throw new Error('SendGrid API key required')
    }

    this.apiKey = config.apiKey
  }

  protected async send(email: string, template: EmailTemplate): Promise<EmailSendResult> {
    try {
      // Dynamic import to avoid requiring sendgrid as dep
      let sgMail: any
      try {
        // @ts-ignore
        sgMail = await import('@sendgrid/mail').then((m: any) => m.default)
      } catch (e) {
        throw new Error('@sendgrid/mail is required for SendGrid provider. Install with: npm install @sendgrid/mail')
      }

      sgMail.setApiKey(this.apiKey)

      const result = await sgMail.send({
        to: email,
        from: `${this.config.fromName || 'Kitium Auth'} <${this.config.from}>`,
        subject: template.subject,
        html: template.html,
        text: template.text
      })

      return {
        success: true,
        messageId: result[0].headers['x-message-id']
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }
}

/**
 * Mailgun Email Provider
 */
export class MailgunEmailProvider extends BaseEmailProvider {
  private apiKey: string
  private domain: string

  constructor(config: EmailConfig) {
    super(config)

    if (!config.apiKey) {
      throw new Error('Mailgun API key required')
    }

    if (!config.domain) {
      throw new Error('Mailgun domain required')
    }

    this.apiKey = config.apiKey
    this.domain = config.domain
  }

  protected async send(email: string, template: EmailTemplate): Promise<EmailSendResult> {
    try {
      let FormData: any
      try {
        // @ts-ignore
        FormData = (await import('form-data')).default
      } catch (e) {
        throw new Error('form-data is required for Mailgun provider. Install with: npm install form-data')
      }

      const form = new FormData()

      form.append('from', `${this.config.fromName || 'Kitium Auth'} <${this.config.from}>`)
      form.append('to', email)
      form.append('subject', template.subject)
      form.append('html', template.html)
      form.append('text', template.text)

      const response = await fetch(`https://api.mailgun.net/v3/${this.domain}/messages`, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${Buffer.from(`api:${this.apiKey}`).toString('base64')}`
        },
        body: form as any
      })

      if (!response.ok) {
        throw new Error(`Mailgun error: ${response.statusText}`)
      }

      const result = await response.json() as any
      return {
        success: true,
        messageId: result.id
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }
}

/**
 * Resend Email Provider
 */
export class ResendEmailProvider extends BaseEmailProvider {
  private apiKey: string

  constructor(config: EmailConfig) {
    super(config)

    if (!config.apiKey) {
      throw new Error('Resend API key required')
    }

    this.apiKey = config.apiKey
  }

  protected async send(email: string, template: EmailTemplate): Promise<EmailSendResult> {
    try {
      const response = await fetch('https://api.resend.com/emails', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from: `${this.config.fromName || 'Kitium Auth'} <${this.config.from}>`,
          to: email,
          subject: template.subject,
          html: template.html,
          text: template.text
        })
      })

      if (!response.ok) {
        throw new Error(`Resend error: ${response.statusText}`)
      }

      const result = await response.json() as any
      return {
        success: true,
        messageId: result.id
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }
}

/**
 * Mock Email Provider (for testing)
 */
export class MockEmailProvider extends BaseEmailProvider {
  private messages: Array<{ to: string; template: EmailTemplate }> = []

  async send(email: string, template: EmailTemplate): Promise<EmailSendResult> {
    this.messages.push({ to: email, template })
    return {
      success: true,
      messageId: `mock-${Date.now()}-${Math.random()}`
    }
  }

  getMessages() {
    return this.messages
  }

  clearMessages() {
    this.messages = []
  }

  getLastMessage(email: string) {
    return this.messages.filter(m => m.to === email).pop()
  }
}

/**
 * Factory function to create email provider
 */
export function createEmailProvider(config: EmailConfig): IEmailProvider {
  switch (config.provider) {
    case 'smtp':
      return new SMTPEmailProvider(config)
    case 'sendgrid':
      return new SendGridEmailProvider(config)
    case 'mailgun':
      return new MailgunEmailProvider(config)
    case 'resend':
      return new ResendEmailProvider(config)
    case 'mock':
      return new MockEmailProvider(config)
    default:
      throw new Error(`Unknown email provider: ${config.provider}`)
  }
}
